/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Icon from '@mui/material/Icon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';

// Authentication layout components
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';

const initAttr = {
  cash: '',
  transfer: '',
  credit: '',
  total: '',
  nettotal: '',
};

function PosPayment({
  reload,
  setBlockScan,
  isDisabled,
  sizeButton,
  isMobileDevice,
  buttonLoading,
  setButtonLoading,
}) {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [notify, setNotify] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [modalOpen, setModalOpen] = useState(false);
  const btnRef = useRef(null);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const close = () => {
    setBlockScan(false);
    setModalOpen(false);
  };

  const changeTotal = (type) => {
    const cash = type === 'CASH' ? 0 : attr.cash * 1;
    const transfer = type === 'TRANSFER' ? 0 : attr.transfer * 1;
    const credit = type === 'CREDIT' ? 0 : attr.credit * 1;
    const nettotal = attr.nettotal * 1;
    return nettotal - cash - transfer - credit;
  };

  const changeTotalInit = (item) => {
    const cash = item.pay_cash * 1;
    const transfer = item.pay_transfer * 1;
    const credit = item.pay_credit * 1;
    const nettotal = item.nettotal * 1;
    return nettotal - cash - transfer - credit;
  };

  const changeTotalCash = (inp) => {
    const cash = inp * 1;
    const transfer = attr.transfer * 1;
    const credit = attr.credit * 1;
    const nettotal = attr.nettotal * 1;
    return nettotal - cash - transfer - credit;
  };

  const changeTotalTransfer = (inp) => {
    const cash = attr.cash * 1;
    const transfer = inp * 1;
    const credit = attr.credit * 1;
    const nettotal = attr.nettotal * 1;
    return nettotal - cash - transfer - credit;
  };

  const changeTotalCredit = (inp) => {
    const cash = attr.cash * 1;
    const transfer = attr.transfer * 1;
    const credit = inp * 1;
    const nettotal = attr.nettotal * 1;
    return nettotal - cash - transfer - credit;
  };

  const insert = (type) => {
    const total = changeTotal(type);

    if (type === 'CASH') setAttr({ ...attr, cash: total, total: 0 });
    else if (type === 'TRANSFER') setAttr({ ...attr, transfer: total, total: 0 });
    else if (type === 'CREDIT') setAttr({ ...attr, credit: total, total: 0 });
  };

  const checkout = () => {
    setLoading(true);
    setBlockScan(true);

    FetchData('OPTIONS', `/${mod}/checkout`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          const total = changeTotalInit(result.cart);

          setAttr({
            ...initAttr,
            nettotal: result.cart.nettotal,
            cash: result.cart.pay_cash === '0.00' ? '' : result.cart.pay_cash,
            transfer: result.cart.pay_transfer === '0.00' ? '' : result.cart.pay_transfer,
            credit: result.cart.pay_credit === '0.00' ? '' : result.cart.pay_credit,
            total: total,
          });
          setModalOpen(true);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const save = () => {
    setSaveLoading(true);

    FetchData('OPTIONS', `/${mod}/split`, app.token, {
      lang: app.lang,
      pay_cash: attr.cash,
      pay_transfer: attr.transfer,
      pay_credit: attr.credit,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          close();
          reload();
          showNotify(t(`alert.savecomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const cancel = () => {
    setSaveLoading(true);

    FetchData('OPTIONS', `/${mod}/splitcancel`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          close();
          reload();
          showNotify(t(`alert.savecomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          แยกชำระ
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer sx={{ boxShadow: 0 }}>
            <Table aria-label='datatable table'>
              <TableBody>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h4' className='nowrap'>
                      มูลค่าชำระ :
                    </MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h4' className='nowrap' color='success'>
                      {Func.numberDisplay(attr.nettotal, 2)}
                    </MDTypography>
                  </TableCell>
                  <TableCell colSpan={3}>
                    <MDTypography variant='h4'>฿</MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right' width={200}>
                    <MDTypography variant='h4' className='nowrap'>
                      เงินสด :
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDInput
                      value={attr.cash}
                      type='number'
                      onChange={(e) =>
                        setAttr({
                          ...attr,
                          cash: e.target.value,
                          total: changeTotalCash(e.target.value),
                        })
                      }
                      onFocus={(e) => e.target.select()}
                      variant='standard'
                      fullWidth
                      inputProps={{
                        placeholder: '0.00',
                        maxLength: 20,
                        autoComplete: 'off',
                        className: 'text-right',
                        sx: { minWidth: 80 },
                      }}
                    />
                  </TableCell>
                  <TableCell width={40}>
                    <MDButton
                      variant='text'
                      color='success'
                      iconOnly
                      onClick={() => insert('CASH')}
                    >
                      <i className='fad fa-plus-circle' />
                    </MDButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right' width={200}>
                    <MDTypography variant='h4' className='nowrap'>
                      เงินโอน :
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDInput
                      value={attr.transfer}
                      type='number'
                      onChange={(e) =>
                        setAttr({
                          ...attr,
                          transfer: e.target.value,
                          total: changeTotalTransfer(e.target.value),
                        })
                      }
                      onFocus={(e) => e.target.select()}
                      variant='standard'
                      fullWidth
                      inputProps={{
                        placeholder: '0.00',
                        maxLength: 20,
                        autoComplete: 'off',
                        className: 'text-right',
                        sx: { minWidth: 80 },
                      }}
                    />
                  </TableCell>
                  <TableCell width={40}>
                    <MDButton
                      variant='text'
                      color='success'
                      iconOnly
                      onClick={() => insert('TRANSFER')}
                    >
                      <i className='fad fa-plus-circle' />
                    </MDButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right' width={200}>
                    <MDTypography variant='h4' className='nowrap'>
                      บัตรเครดิต :
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDInput
                      value={attr.credit}
                      type='number'
                      onChange={(e) =>
                        setAttr({
                          ...attr,
                          credit: e.target.value,
                          total: changeTotalCredit(e.target.value),
                        })
                      }
                      onFocus={(e) => e.target.select()}
                      variant='standard'
                      fullWidth
                      inputProps={{
                        placeholder: '0.00',
                        maxLength: 20,
                        autoComplete: 'off',
                        className: 'text-right',
                        sx: { minWidth: 80 },
                      }}
                    />
                  </TableCell>
                  <TableCell width={40}>
                    <MDButton
                      variant='text'
                      color='success'
                      iconOnly
                      onClick={() => insert('CREDIT')}
                    >
                      <i className='fad fa-plus-circle' />
                    </MDButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h4' className='nowrap'>
                      มูลค่าคงเหลือ :
                    </MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h4' className='nowrap' color='error'>
                      {Func.numberDisplay(attr.total, 2)}
                    </MDTypography>
                  </TableCell>
                  <TableCell colSpan={2}>
                    <MDTypography variant='h4'>฿</MDTypography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={cancel} disabled={saveLoading}>
                {saveLoading ? <CircularProgress size={18} color='inherit' /> : <Icon>close</Icon>}
                &nbsp;ยกเลิก
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='text' color='info' onClick={save} disabled={saveLoading}>
                {saveLoading ? <CircularProgress size={18} color='inherit' /> : <Icon>save</Icon>}{' '}
                &nbsp; บันทึก
              </MDButton>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <MDBox>
      <Tooltip title={isDisabled ? '' : 'KEY : P'}>
        <MDButton
          variant='contained'
          color='dark'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={checkout}
          disabled={isDisabled || isMobileDevice || loading || buttonLoading}
          ref={btnRef}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-page-break' />
          )}
          &nbsp;
          {isMobileDevice ? 'ไม่รองรับบนมือถือ' : 'แยกชำระ'}
        </MDButton>
      </Tooltip>
      {renderOpen()}
      {notify}
    </MDBox>
  );
}

PosPayment.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  sizeButton: PropTypes.number.isRequired,
  isMobileDevice: PropTypes.bool.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosPayment;
